import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { closeHoldAndWinResumePopup, setUserInterfaceDisabled } from "../../../State/actions";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import i18next from "i18next";

/**
 * HoldAndWinReloadController - Handles Hold and Win reload/resume dialog
 * Shows when user reloads page during active Hold and Win session
 * Displays remaining respins count using spine skins (1, 2, or 3)
 */
class HoldAndWinReloadController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    respinsLeft
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.respinsLeft = respinsLeft;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.reloadSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;

    // Internal caches for performance optimization
    this._deviceScaleMultiplierCache = null;
    this._fontSizeMultiplierCache = null;
    this._buttonYOffsetCache = null;
    this._availableAnimationsCache = null;
    this._availableSkinsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    // Text containers for bone attachment
    this.youHaveContainer = null;
    this.youHaveText = null;
    this.respinsContainer = null;
    this.respinsText = null;

    // Button attached to spine bone
    this.buttonContainer = null;
    this.buttonText = null;
    this.boneUpdateTicker = null;

    this.initializeReloadSpine();
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.reloadSpine || !this.dimensions || !this.initialized) return;

    // Safety check: ensure spine is not destroyed
    if (!this.reloadSpine.scale || this.reloadSpine.destroyed) return;

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device/orientation-specific multipliers
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    scale *= deviceMultiplier;

    this.reloadSpine.scale.set(scale);
    // Position at dead center (parent container is already centered)
    this.reloadSpine.x = 0;
    this.reloadSpine.y = 0;
  }

  /**
   * Get device-specific scale multiplier for spine animations
   */
  getDeviceScaleMultiplier() {
    if (!this.dimensionsContext) {
      return 1.0;
    }

    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._deviceScaleMultiplierCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._deviceScaleMultiplierCache;
    }

    // All devices return 1.0 currently, but cache for consistency
    const multiplier = 1.0;

    // Cache the result
    this._deviceScaleMultiplierCache = multiplier;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multiplier;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   * OPTIMIZED: Only recalculate when values actually change
   */
  updateDimensions(dimensionsContext, symbolHeight) {
    // Check if any values actually changed to avoid unnecessary recalculations
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed
    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    // Invalidate device config caches if device/orientation changed
    if (deviceChanged) {
      this._deviceScaleMultiplierCache = null;
      this._fontSizeMultiplierCache = null;
      this._buttonYOffsetCache = null;
      this._lastDevice = null;
      this._lastOrientation = null;
    }

    // Update button text if device changed (tap vs click)
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }

    // CRITICAL: Always update spine positioning whenever dimensions change
    if (
      this.reloadSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      // Ensure we have valid symbolHeight before positioning
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  /**
   * Initialize the Hold and Win reload spine
   */
  initializeReloadSpine() {
    try {
      this.reloadSpine = this.createReloadSpine();

      if (!this.reloadSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.reloadSpine.skeleton) missingComponents.push("skeleton");
      if (!this.reloadSpine.state) missingComponents.push("state");
      if (!this.reloadSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      // Set skin based on respins left (1, 2, or 3)
      this.setSkinForRespins();

      this.container.addChild(this.reloadSpine);
      this.reloadSpine.alpha = 0.001;
      this.reloadSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.reloadSpine.parent !== this.container) {
        return;
      }

      this.initialized = true;
      this.warmUpReloadSpine();

      // Invalidate animations and skins caches when spine is initialized
      this._availableAnimationsCache = null;
      this._availableSkinsCache = null;

      this.initializeYouHaveText();
      this.initializeRespinsText();
      this.initializeButton();
    } catch (error) {
      // Fail silently
    }
  }

  /**
   * Create reload spine from loaded assets
   */
  createReloadSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "hns_reload-skeleton";
      const atlasAlias = "hns_reload-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const reloadSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!reloadSpine?.skeleton?.data?.animations) {
        return null;
      }

      // Invalidate caches when spine is recreated
      this._availableAnimationsCache = null;
      this._availableSkinsCache = null;

      return reloadSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Set spine skin based on respins left (1, 2, or 3)
   */
  setSkinForRespins() {
    if (!this.reloadSpine?.skeleton) return;

    // Clamp respins to 1-3 range (spine only has skins for 1, 2, 3)
    const skinNumber = Math.max(1, Math.min(3, this.respinsLeft));
    const skinName = String(skinNumber);

    try {
      this.reloadSpine.skeleton.setSkinByName(skinName);
      this.reloadSpine.skeleton.setSlotsToSetupPose();
    } catch (error) {
      // Fallback to default skin
      try {
        this.reloadSpine.skeleton.setSkinByName("default");
        this.reloadSpine.skeleton.setSlotsToSetupPose();
      } catch (fallbackError) {
      }
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Skip warm-up entirely - let spine initialize naturally during first play
   * This avoids physics constraint errors
   */
  warmUpReloadSpine() {
    if (!this.reloadSpine?.state) return;

    // Don't warm up - just set alpha to hidden
    // Spine will initialize properly when startSequence() plays the first animation
    this.reloadSpine.alpha = 0.001;
  }

  /**
   * Get font size multiplier based on device and orientation
   */
  getFontSizeMultiplier() {
    if (!this.dimensionsContext) {
      return 1.0;
    }

    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._fontSizeMultiplierCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._fontSizeMultiplierCache;
    }

    let multiplier;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multiplier = 0.8;
      } else {
        multiplier = 0.7;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multiplier = 0.9;
      } else {
        multiplier = 0.8;
      }
    } else {
      // Desktop
      multiplier = 1.0;
    }

    // Cache the result
    this._fontSizeMultiplierCache = multiplier;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multiplier;
  }

  /**
   * Get device-specific Y offset for button positioning
   * Adjusts button position based on device and orientation
   * Matches WinAnimationController and HoldAndWinIntroController button offset values
   */
  getButtonYOffset() {
    if (!this.dimensionsContext) {
      return 100;
    }

    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._buttonYOffsetCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._buttonYOffsetCache;
    }

    let offset;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        offset = 335; // Mobile portrait offset
      } else {
        offset = 120; // Mobile landscape offset
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        offset = 290; // Tablet portrait offset
      } else {
        offset = 185; // Tablet landscape offset
      }
    } else {
      offset = 100; // Desktop offset
    }

    // Cache the result
    this._buttonYOffsetCache = offset;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return offset;
  }

  /**
   * Initialize "YOU HAVE" text and attach to bone
   */
  initializeYouHaveText() {
    if (!this.reloadSpine?.skeleton) return;

    // Find the bone
    const youHaveBone = this.reloadSpine.skeleton.findBone("retrigger_text_youhave");
    if (!youHaveBone) {
      return;
    }

    // Create container as child of spine
    this.youHaveContainer = new PIXI.Container();
    this.youHaveContainer.name = "you_have_text_attached_to_bone";
    this.youHaveContainer.alpha = 1;
    this.youHaveContainer.zIndex = 1002;

    // Get translated text
    const youHaveText = i18next.t("you_have");

    // Calculate font size with device scaling
    const baseFontSize = 140;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Create text style - same as "YOU WON" (lighter gradient)
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Troika, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "normal",
      fill: ["#fff4be", "#ffdf46"], // Gradient: lighter on top, darker on bottom
      fillGradientType: PIXI.TEXT_GRADIENT.LINEAR_VERTICAL,
      stroke: "#fff7d0", // Lighter outline
      strokeThickness: 3,
      dropShadow: true,
      dropShadowColor: "#a92400", // Sharp bottom shadow
      dropShadowDistance: 5,
      dropShadowAngle: Math.PI / 2,
      dropShadowBlur: 0, // Sharp (no blur)
      dropShadowAlpha: 1,
      align: "center",
    });

    this.youHaveText = new PIXI.Text(youHaveText, textStyle);
    this.youHaveText.anchor.set(0.5);
    this.youHaveText.x = 0;
    this.youHaveText.y = 0;

    this.youHaveContainer.addChild(this.youHaveText);
    this.reloadSpine.addChild(this.youHaveContainer);
    this.reloadSpine.sortableChildren = true;
  }

  /**
   * Initialize "RESPINS LEFT" text and attach to bone
   */
  initializeRespinsText() {
    if (!this.reloadSpine?.skeleton) return;

    // Find the bone
    const respinsBone = this.reloadSpine.skeleton.findBone("retrigger_text_respins");
    if (!respinsBone) {
      return;
    }

    // Create container as child of spine
    this.respinsContainer = new PIXI.Container();
    this.respinsContainer.name = "respins_left_text_attached_to_bone";
    this.respinsContainer.alpha = 1;
    this.respinsContainer.zIndex = 1003;

    // Get translated text with pluralization
    const respinsText = i18next.t("respins_left_popup", { count: this.respinsLeft });

    // Calculate font size with device scaling
    const baseFontSize = 100;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Create text style - same as "YOU WON" (lighter gradient)
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Troika, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "normal",
      fill: ["#fff4be", "#ffdf46"], // Gradient: lighter on top, darker on bottom
      fillGradientType: PIXI.TEXT_GRADIENT.LINEAR_VERTICAL,
      stroke: "#fff7d0", // Lighter outline
      strokeThickness: 3,
      dropShadow: true,
      dropShadowColor: "#a92400", // Sharp bottom shadow
      dropShadowDistance: 5,
      dropShadowAngle: Math.PI / 2,
      dropShadowBlur: 0, // Sharp (no blur)
      dropShadowAlpha: 1,
      align: "center",
    });

    this.respinsText = new PIXI.Text(respinsText, textStyle);
    this.respinsText.anchor.set(0.5);
    this.respinsText.x = 0;
    this.respinsText.y = 0;

    this.respinsContainer.addChild(this.respinsText);
    this.reloadSpine.addChild(this.respinsContainer);
    this.reloadSpine.sortableChildren = true;
  }

  /**
   * Initialize button and attach it to spine's "text_tap_to_contiune" bone
   *
   * The button is a child of the spine and follows the bone's transform.
   * Position, rotation, and scale are controlled by the bone animation.
   */
  initializeButton() {
    if (!this.reloadSpine || !this.reloadSpine.skeleton) {
      return;
    }

    const buttonBone = this.reloadSpine.skeleton.findBone("text_tap_to_contiune");
    if (!buttonBone) {
      return;
    }

    // Create button container as child of spine
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "reload_continue_button_attached_to_bone";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001; // Render above other elements

    // Get translated button text based on device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = i18next.t(translationKey).toUpperCase();

    // Calculate font size with device-specific multiplier
    const baseFontSize = 70;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Create text style (using Noto Sans font)
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "700",
      fill: "#ffffff",
      dropShadow: true,
      dropShadowColor: "#000000",
      dropShadowDistance: 4,
      dropShadowBlur: 4,
      dropShadowAlpha: 0.8,
      align: "center",
    });

    // Create PIXI.Text
    this.buttonText = new PIXI.Text(buttonText, textStyle);
    this.buttonText.anchor.set(0.5);
    this.buttonText.x = 0;
    this.buttonText.y = 0;

    // Scale down the button (0.7x smaller, matching intro)
    this.buttonText.scale.set(0.7);

    this.buttonContainer.addChild(this.buttonText);
    this.reloadSpine.addChild(this.buttonContainer);
    this.reloadSpine.sortableChildren = true;
  }

  /**
   * Update "YOU HAVE" text position from bone
   */
  updateYouHaveFromBone() {
    if (!this.reloadSpine || !this.youHaveContainer || !this.reloadSpine.skeleton) {
      return;
    }

    const youHaveBone = this.reloadSpine.skeleton.findBone("retrigger_text_youhave");
    if (!youHaveBone) {
      return;
    }

    this.youHaveContainer.x = Math.round(youHaveBone.worldX);
    this.youHaveContainer.y = Math.round(youHaveBone.worldY);
    this.youHaveContainer.rotation = (youHaveBone.getWorldRotationX() * Math.PI) / 180;
    this.youHaveContainer.scale.set(
      youHaveBone.getWorldScaleX(),
      youHaveBone.getWorldScaleY()
    );
  }

  /**
   * Update "RESPINS LEFT" text position from bone
   */
  updateRespinsFromBone() {
    if (!this.reloadSpine || !this.respinsContainer || !this.reloadSpine.skeleton) {
      return;
    }

    const respinsBone = this.reloadSpine.skeleton.findBone("retrigger_text_respins");
    if (!respinsBone) {
      return;
    }

    this.respinsContainer.x = Math.round(respinsBone.worldX);
    this.respinsContainer.y = Math.round(respinsBone.worldY);
    this.respinsContainer.rotation = (respinsBone.getWorldRotationX() * Math.PI) / 180;
    this.respinsContainer.scale.set(
      respinsBone.getWorldScaleX(),
      respinsBone.getWorldScaleY()
    );
  }

  /**
   * Sync button transform with spine's "text_tap_to_contiune" bone
   * Called every frame while spine is animating
   * Applies device-specific Y offset for responsive positioning
   */
  updateButtonFromBone() {
    if (
      !this.reloadSpine ||
      !this.buttonContainer ||
      !this.reloadSpine.skeleton
    ) {
      return;
    }

    const buttonBone = this.reloadSpine.skeleton.findBone("text_tap_to_contiune");
    if (!buttonBone) {
      return;
    }

    // Get device-specific Y offset
    const yOffset = this.getButtonYOffset();

    // Apply bone's world transform to button with device-specific Y offset
    this.buttonContainer.x = buttonBone.worldX;
    this.buttonContainer.y = buttonBone.worldY + yOffset;
    this.buttonContainer.rotation =
      (buttonBone.getWorldRotationX() * Math.PI) / 180;
    this.buttonContainer.scale.set(
      buttonBone.getWorldScaleX(),
      buttonBone.getWorldScaleY()
    );
  }

  /**
   * Start syncing text and button with spine bones every frame
   * Uses PIXI.Ticker to update text and button transforms from bones
   */
  startBoneUpdateLoop() {
    if (!this.reloadSpine || !this.youHaveContainer || !this.respinsContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.isPlaying) {
          this.updateYouHaveFromBone();
          this.updateRespinsFromBone();
          this.updateButtonFromBone();
        }
      };

      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop bone update loop
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Get available animations from the spine
   */
  getAvailableAnimations(spine = this.reloadSpine) {
    if (!spine?.skeleton?.data?.animations) {
      return [];
    }

    // Return cached value if available and using default spine
    if (spine === this.reloadSpine && this._availableAnimationsCache) {
      return this._availableAnimationsCache;
    }

    const animations = spine.skeleton.data.animations.map((a) => a.name);

    // Cache the result only if using default spine
    if (spine === this.reloadSpine) {
      this._availableAnimationsCache = animations;
    }

    return animations;
  }

  /**
   * Get available skins from the spine
   */
  getAvailableSkins(spine = this.reloadSpine) {
    if (!spine?.skeleton?.data?.skins) {
      return [];
    }

    // Return cached value if available and using default spine
    if (spine === this.reloadSpine && this._availableSkinsCache) {
      return this._availableSkinsCache;
    }

    const skins = spine.skeleton.data.skins.map((s) => s.name);

    // Cache the result only if using default spine
    if (spine === this.reloadSpine) {
      this._availableSkinsCache = skins;
    }

    return skins;
  }

  /**
   * Update button text based on current device (tap vs click)
   */
  updateButtonText() {
    if (!this.buttonText) return;

    // Get translated button text based on current device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = i18next.t(translationKey).toUpperCase();

    // Update text content
    this.buttonText.text = buttonText;

    // Recalculate font size with device-specific multiplier
    const baseFontSize = 70;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Update font size
    this.buttonText.style.fontSize = finalFontSize;
  }

  /**
   * Show continue button with fade-in animation
   */
  showButton() {
    if (!this.buttonContainer) return;

    anime({
      targets: this.buttonContainer,
      alpha: 1,
      duration: 300,
      easing: "easeOutQuad",
    });
  }

  /**
   * Hide continue button with fade-out animation
   */
  hideButton() {
    if (!this.buttonContainer) return;

    anime({
      targets: this.buttonContainer,
      alpha: 0,
      duration: 300,
      easing: "easeOutQuad",
    });
  }

  /**
   * Start the reload dialog animation sequence
   */
  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.reloadSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;

      // Show overlay instantly (no fade-in animation for reload dialog)
      if (this.overlayRef.current?.setAlpha) {
        this.overlayRef.current.setAlpha(0.925); // Match OVERLAY_BACKGROUND_ALPHA
      }

      // Reset user interaction guards
      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      // Clear any existing timeouts
      if (this.skipProtectionTimeout) {
        clearTimeout(this.skipProtectionTimeout);
        this.skipProtectionTimeout = null;
      }
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      // Hold and Win music should already be playing from the reload state
      // Don't play it again - just play the reload dialog sound
      // Play reload dialog open sound
      soundManager.play(SOUND_NAME.hold_and_win_reload_in);

      // Spine is already positioned correctly at all times
      if (
        this.reloadSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      this.reloadSpine.alpha = 1;
      this.reloadSpine.state.clearTracks();
      this.reloadSpine.state.clearListeners();
      this.reloadSpine.state.apply(this.reloadSpine.skeleton);
      this.reloadSpine.skeleton.setToSetupPose();

      // Update button position immediately before showing to prevent flash
      this.updateButtonFromBone();

      // Start bone update loop for text and button synchronization
      this.startBoneUpdateLoop();

      // SKIP PROTECTION: Block skipping for the first second
      // Show button after 1 second
      this.skipProtectionTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();
          this.isWaitingForClick = true;
        }
      }, 1000);

      // Play IN animation, then loop IDLE animation
      this.reloadSpine.state.setAnimation(0, "in", false);

      // Get IN animation duration
      const inDuration = this.getSpineAnimationDuration("in");

      // After IN completes, start looping IDLE
      const inTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.reloadSpine.state.setAnimation(0, "idle", true);
          resolve(); // Sequence ready, waiting for user click
        }
      }, inDuration);

      // Store timeout for cleanup
      this.currentTimeout = inTimeout;
    });
  }

  /**
   * Get animation duration from spine data
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.reloadSpine ||
      !this.reloadSpine.skeleton ||
      !this.reloadSpine.skeleton.data ||
      !this.reloadSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      const animation = this.reloadSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Handle user click to close animation
   * Optimized debouncing - only 1 click processed, subsequent clicks blocked without creating timeouts
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.reloadSpine?.state) {
      return false;
    }

    // CRITICAL: If debounce timeout exists, block all clicks (prevents timeout buildup)
    // This ensures only ONE click is processed, all others are ignored
    if (this.debounceTimeout) {
      return false;
    }

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // Check if waiting for click
    if (!this.isWaitingForClick) {
      return false;
    }

    // FIRST CLICK ONLY: Create debounce timeout to block subsequent clicks
    // This timeout will be the ONLY timeout created, no matter how many clicks
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // Close the animation (only happens once due to debounce)
    this.playClosingAnimation();

    return true;
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.reloadSpine?.state) return;

    this.isWaitingForClick = false;
    this.hideButton();

    // Play reload dialog close sound
    soundManager.play(SOUND_NAME.hold_and_win_reload_out);

    this.reloadSpine.state.clearListeners();

    this.reloadSpine.state.setAnimation(0, "out", false);
    this.reloadSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "out" && this.isPlaying) {
          this.finishAnimation();
        }
      },
    });
  }

  /**
   * Finish the animation and clean up
   * Triggers IntroTransitionOverlay to reveal the Hold and Win game
   */
  finishAnimation() {
    if (!this.reloadSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.reloadSpine.alpha = 0.001;

    // Keep Hold and Win music playing (don't stop it!)
    // The game is already in Hold and Win mode

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Reset user interaction guards
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // Fade out overlay and close dialog
    this.overlayRef.current?.fadeOutBackground(() => {
      if (this.dispatch) {
        this.dispatch(closeHoldAndWinResumePopup());
        // Enable UI after reload dialog closes
        this.dispatch(setUserInterfaceDisabled(false));
      }

      // Play feature background music when reload dialog closes
      // This handles the case where user reloaded during Hold and Win
      // and we skipped the scene transition (which normally plays this music)
      soundManager.currentBackgroundMusic = SOUND_NAME.feature_background_music;
      soundManager.fadeIn(SOUND_NAME.feature_background_music, 500);
    });
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Cleanup text containers
    if (this.youHaveContainer) {
      this.youHaveContainer.destroy({ children: true });
      this.youHaveContainer = null;
      this.youHaveText = null;
    }

    if (this.respinsContainer) {
      this.respinsContainer.destroy({ children: true });
      this.respinsContainer = null;
      this.respinsText = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      if (this.buttonText) {
        this.buttonText.destroy();
        this.buttonText = null;
      }
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
    }

    if (this.reloadSpine) {
      this.reloadSpine.state?.clearListeners();
      this.reloadSpine.state?.clearTracks();
      if (this.reloadSpine.state) this.reloadSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.reloadSpine)) {
        this.container.removeChild(this.reloadSpine);
      }
      this.reloadSpine.destroy();
      this.reloadSpine = null;
    }

    this.initialized = false;

    // Clear caches
    this._deviceScaleMultiplierCache = null;
    this._fontSizeMultiplierCache = null;
    this._buttonYOffsetCache = null;
    this._availableAnimationsCache = null;
    this._availableSkinsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;
  }
}

export default HoldAndWinReloadController;
